import React, { forwardRef, useState } from 'react';

/**
 * Input Component
 *
 * A beautiful, accessible input with label, error states, and icons.
 * Designed for mobile-first with large touch targets.
 */

export const Input = forwardRef(function Input({
  label,
  error,
  hint,
  icon,
  iconPosition = 'left',
  className = '',
  containerClassName = '',
  type = 'text',
  ...props
}, ref) {
  const [isFocused, setIsFocused] = useState(false);

  const hasIcon = !!icon;
  const paddingLeft = hasIcon && iconPosition === 'left' ? 'pl-11' : 'pl-4';
  const paddingRight = hasIcon && iconPosition === 'right' ? 'pr-11' : 'pr-4';

  return (
    <div className={`w-full ${containerClassName}`}>
      {label && (
        <label className="block text-sm font-medium text-warm-700 mb-1.5">
          {label}
        </label>
      )}

      <div className="relative">
        {hasIcon && iconPosition === 'left' && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-warm-400 pointer-events-none">
            {icon}
          </div>
        )}

        <input
          ref={ref}
          type={type}
          className={`
            input
            ${paddingLeft}
            ${paddingRight}
            ${error ? 'input-error animate-shake' : ''}
            ${isFocused ? 'ring-2 ring-coral-100' : ''}
            ${className}
          `.trim().replace(/\s+/g, ' ')}
          onFocus={(e) => {
            setIsFocused(true);
            props.onFocus?.(e);
          }}
          onBlur={(e) => {
            setIsFocused(false);
            props.onBlur?.(e);
          }}
          {...props}
        />

        {hasIcon && iconPosition === 'right' && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-warm-400 pointer-events-none">
            {icon}
          </div>
        )}
      </div>

      {error && (
        <p className="mt-1.5 text-sm text-red-500 flex items-center gap-1">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {error}
        </p>
      )}

      {hint && !error && (
        <p className="mt-1.5 text-sm text-warm-500">
          {hint}
        </p>
      )}
    </div>
  );
});

/**
 * TextArea Component
 */
export const TextArea = forwardRef(function TextArea({
  label,
  error,
  hint,
  className = '',
  containerClassName = '',
  rows = 4,
  ...props
}, ref) {
  return (
    <div className={`w-full ${containerClassName}`}>
      {label && (
        <label className="block text-sm font-medium text-warm-700 mb-1.5">
          {label}
        </label>
      )}

      <textarea
        ref={ref}
        rows={rows}
        className={`
          input resize-none
          ${error ? 'input-error animate-shake' : ''}
          ${className}
        `.trim().replace(/\s+/g, ' ')}
        {...props}
      />

      {error && (
        <p className="mt-1.5 text-sm text-red-500 flex items-center gap-1">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {error}
        </p>
      )}

      {hint && !error && (
        <p className="mt-1.5 text-sm text-warm-500">
          {hint}
        </p>
      )}
    </div>
  );
});

/**
 * SearchInput - Input optimized for search
 */
export const SearchInput = forwardRef(function SearchInput({
  placeholder = 'Search...',
  onClear,
  value,
  className = '',
  ...props
}, ref) {
  const hasValue = value && value.length > 0;

  return (
    <div className="relative w-full">
      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-warm-400 pointer-events-none">
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>

      <input
        ref={ref}
        type="search"
        value={value}
        placeholder={placeholder}
        className={`
          input pl-11 pr-10
          ${className}
        `.trim().replace(/\s+/g, ' ')}
        {...props}
      />

      {hasValue && onClear && (
        <button
          type="button"
          onClick={onClear}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-warm-400 hover:text-warm-600 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
});

export default Input;
